import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Cloudflare.css";
import cloudflareLogo from "../../assets/images/partners/Cloudflare.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const networkPoPs = [
  { city: "New York, US", ms: "2ms" }, { city: "London, UK", ms: "1ms" },
  { city: "Frankfurt, DE", ms: "2ms" }, { city: "Singapore, SG", ms: "1ms" },
  { city: "Tokyo, JP", ms: "2ms" }, { city: "Sydney, AU", ms: "3ms" },
  { city: "Dubai, UAE", ms: "2ms" }, { city: "São Paulo, BR", ms: "3ms" },
];

const saseCards = [
  { icon: "🚀", title: "Zero Trust Network Access", desc: "Replace legacy VPNs with Cloudflare ZTNA. Every user and device is verified before gaining access to any application — on-premises or cloud-hosted — with least-privilege access enforced at the session level." },
  { icon: "🌐", title: "Secure Web Gateway", desc: "Inspect and filter all web traffic to block malware, phishing, and data exfiltration. DNS filtering, HTTP/S inspection, and shadow IT visibility in one unified product without deploying on-premises appliances." },
  { icon: "☁️", title: "Cloud Access Security Broker", desc: "Gain visibility and control over SaaS application usage across your organization. Enforce data loss prevention and identity-based access policies for Microsoft 365, Google Workspace, Salesforce, and 1,000+ SaaS apps." },
  { icon: "📧", title: "Email Security — Area 1", desc: "Pre-delivery email protection that stops phishing at the source. Cloudflare Area 1 uses web crawlers to detect and block malicious infrastructure before threats reach user inboxes — stopping BEC, spear phishing, and impersonation." },
  { icon: "🔗", title: "Magic WAN & SD-WAN", desc: "Replace MPLS and SD-WAN appliances with Cloudflare's global network backbone. Connect offices, data centers, and cloud properties with consistent security policy enforcement and performance optimization without hardware appliances." },
  { icon: "📡", title: "Network Firewall as a Service", desc: "Cloudflare Magic Firewall delivers a cloud-native network firewall — filtering traffic at every Cloudflare edge location globally, applying consistent policy across branch offices, remote workers, and cloud infrastructure." },
];

const ztArchBlocks = [
  { label: "Identity Layer", name: "Cloudflare Access + IdP Integration", desc: "Okta, Azure AD, Google Workspace — every request authenticated" },
  { label: "Device Layer", name: "Device Posture Verification", desc: "WARP client enforces endpoint health before granting access" },
  { label: "Application Layer", name: "Application-Level Zero Trust", desc: "Per-application micro-segmentation replacing network trust" },
  { label: "Edge Layer", name: "Cloudflare One — Global Edge Enforcement", desc: "320+ cities enforce policy at the network edge before traffic reaches applications" },
];

const ztPillars = [
  { icon: "🔐", title: "Never Trust, Always Verify", desc: "Every access request — from any user, device, or location — is verified before session establishment, with no implicit trust based on network location." },
  { icon: "👤", title: "Identity-Aware Proxy", desc: "Cloudflare Access acts as an identity-aware proxy — sitting between users and applications, enforcing identity, device posture, and contextual access policies." },
  { icon: "🔒", title: "Continuous Session Validation", desc: "Trust isn't assumed at login — it's re-evaluated continuously. Cloudflare revokes access dynamically when device posture or behavioral signals indicate risk." },
  { icon: "📊", title: "Complete Access Logging", desc: "Every access event is logged — user, device, location, application, and outcome — enabling security teams to audit access patterns and detect anomalies." },
];

const perfFeatures = [
  "Anycast CDN with 320+ PoP locations serving content from the user's nearest edge node",
  "Argo Smart Routing optimizes TCP connections across Cloudflare's private network backbone",
  "HTTP/3 and QUIC support delivering faster connections for mobile and high-latency environments",
  "Cloudflare Images and Stream for optimized media delivery at global edge scale",
  "Cloudflare Fonts serving Google Fonts from Cloudflare's network without external DNS lookups",
  "Rocket Loader asynchronously loads JavaScript, improving page performance for JS-heavy applications",
];

const workerItems = [
  { icon: "⚡", title: "Cloudflare Workers", desc: "Deploy serverless JavaScript, TypeScript, or WebAssembly at the Cloudflare edge — running code 0ms from users in 320+ cities without cold starts, containers, or server management." },
  { icon: "🗄️", title: "Workers KV — Edge Key-Value", desc: "Globally distributed key-value storage accessible from Workers — enabling edge applications to store and retrieve data with sub-millisecond reads from anywhere in the world." },
  { icon: "📦", title: "R2 Object Storage", desc: "S3-compatible object storage without egress fees — store and serve static assets, user uploads, and application data at Cloudflare's edge without per-GB data transfer costs." },
  { icon: "🔄", title: "Durable Objects", desc: "Stateful edge computing that co-locates application state with computation — enabling coordination, real-time collaboration, and stateful serverless applications at the edge." },
  { icon: "🤖", title: "AI Gateway", desc: "Route AI inference requests through Cloudflare's network — gaining observability, caching, rate limiting, and cost control across OpenAI, Anthropic, and other AI model providers." },
];

const ddosCaps = [
  { icon: "🛡️", title: "Unmetered DDoS Mitigation", desc: "Cloudflare absorbs DDoS attacks of any size at no additional cost — with no traffic caps, bandwidth limits, or overage charges regardless of attack volume or duration." },
  { icon: "🤖", title: "Bot Management", desc: "Differentiate between legitimate automated traffic (search crawlers, API integrations) and malicious bots using ML-based fingerprinting and behavioral analysis without blocking real users." },
  { icon: "⚡", title: "Web Application Firewall", desc: "OWASP Top 10, zero-day, and custom rule protection with Cloudflare's managed ruleset — automatically updated as new attack patterns emerge with no signature management required." },
  { icon: "🔐", title: "API Shield", desc: "Positive security model for API protection — discovering all API endpoints, enforcing schemas, detecting anomalous usage patterns, and blocking API abuse before it reaches origin infrastructure." },
  { icon: "📊", title: "Rate Limiting", desc: "Configurable rate limiting rules protecting against credential stuffing, layer 7 floods, and API abuse — with flexible matching on URL, IP, ASN, or custom request characteristics." },
];

const emailFeatures = [
  "Pre-delivery phishing detection using Cloudflare's global web crawlers identifying threat infrastructure before attacks launch",
  "Business Email Compromise (BEC) protection detecting spoofed executive impersonation attempts",
  "Attachment sandboxing and link rewriting protecting against malicious content delivery",
  "Native Microsoft 365 and Google Workspace integration — deploys in minutes without MX record changes",
  "Outbound email security preventing your domain from being used as a phishing source",
  "Retro scanning — detecting and remediating historical phishing emails already delivered to inboxes",
];

const analyticsCards = [
  { icon: "📊", title: "Security Analytics", desc: "Real-time dashboard of all security events — firewall triggers, bot detections, DDoS mitigations, and threat signals — across every Cloudflare-protected property in one view." },
  { icon: "⚡", title: "Performance Insights", desc: "End-to-end performance monitoring — cache hit rates, TTFB measurements, page load timing by geography, and Core Web Vitals tracking across your Cloudflare-proxied applications." },
  { icon: "🔍", title: "Traffic Intelligence", desc: "Deep traffic analysis showing visitor geography, device types, browser distribution, and request patterns — enabling informed decisions about routing, caching, and security policy configuration." },
  { icon: "🤖", title: "Bot Traffic Analysis", desc: "Classify automated traffic by type — search engine crawlers, monitoring services, malicious bots — and understand the composition of your traffic to optimize security without blocking legitimate automation." },
  { icon: "🌐", title: "Zero Trust Analytics", desc: "SASE and Zero Trust activity logging — access events, blocked connections, identity-based traffic, and device posture violations across your entire Cloudflare One deployment." },
  { icon: "📈", title: "Logpush & SIEM Integration", desc: "Stream all Cloudflare logs to your SIEM in real-time — Splunk, Microsoft Sentinel, Datadog, CrowdStrike — enabling correlation with endpoint and identity telemetry for complete security visibility." },
];

const industries = [
  { icon: "🏦", name: "Financial Services", desc: "DDoS protection, WAF, Zero Trust access, and API security for trading platforms, banking portals, and fintech applications serving millions of users." },
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-aligned security for patient portals, telehealth platforms, and healthcare APIs — with Zero Trust access for clinical staff and external partners." },
  { icon: "🛒", name: "Retail & eCommerce", desc: "Bot management protecting loyalty programs from account takeover, CDN acceleration for peak traffic, and payment page WAF protection for PCI compliance." },
  { icon: "🎓", name: "Education", desc: "Student data protection, secure remote learning platforms, and Zero Trust access for distributed faculty — with Cloudflare for Teams free tier for qualifying institutions." },
  { icon: "🏛️", name: "Government", desc: "FedRAMP-authorized Cloudflare for federal agencies — with NIST-aligned Zero Trust architecture, FIPS-compliant encryption, and .gov CDN acceleration." },
  { icon: "🏢", name: "Enterprise SaaS", desc: "Multi-tenant application security, API protection, global performance, and developer platform (Workers) for SaaS companies serving enterprise customers at global scale." },
  { icon: "🏭", name: "Manufacturing", desc: "Industrial IoT security, supply chain partner access via Zero Trust, and OT-adjacent web application protection for manufacturing operations portals." },
  { icon: "⚡", name: "Energy & Utilities", desc: "Critical infrastructure protection, employee remote access via ZTNA, and customer portal WAF for regulated energy companies and public utilities." },
];

const whyReasons = [
  { num: "01", title: "Cloudflare Authorized Partner", desc: "Direct Cloudflare partnership providing access to the full product portfolio, pre-sales technical engineering support, and competitive partner pricing for enterprise Cloudflare deployments." },
  { num: "02", title: "SASE Migration Expertise", desc: "ALG Data Guard architects Cloudflare One SASE migrations — replacing VPN infrastructure, proxy appliances, and legacy firewalls with a phased, risk-managed transition to cloud-native Zero Trust." },
  { num: "03", title: "DNS & Domain Management", desc: "Cloudflare DNS configuration, domain migration, WAF and CDN activation, and SSL certificate management delivered by engineers with production Cloudflare deployment experience." },
  { num: "04", title: "Workers & Edge Development", desc: "Cloudflare Workers development for edge compute use cases — redirect logic, A/B testing, authentication middleware, and API gateway implementations running on Cloudflare's edge." },
  { num: "05", title: "SIEM & Security Integration", desc: "Cloudflare Logpush configuration integrating Cloudflare telemetry with your SIEM platform — enabling security analytics that correlate Cloudflare events with endpoint and identity data." },
];

const faqData = [
  { q: "What is Cloudflare One and how does it compare to traditional SASE vendors?", a: "Cloudflare One is Cloudflare's SASE (Secure Access Service Edge) platform — converging network security (Zero Trust Network Access, Secure Web Gateway, CASB, Email Security, network firewall) with network connectivity (Magic WAN, SD-WAN) on Cloudflare's global edge network. Traditional SASE vendors like Zscaler, Netskope, or Palo Alto Prisma Access built SASE platforms assembled from acquired products running on third-party cloud providers. Cloudflare One is built natively on Cloudflare's own global network — the same infrastructure that has protected millions of websites for 15+ years. Key differentiator: Cloudflare's network is also a CDN and performance network, meaning Zero Trust traffic benefits from Cloudflare's global routing optimization rather than backhauling traffic to security cloud data centers. For organizations evaluating SASE platforms, Cloudflare One is particularly compelling for: organizations that already use Cloudflare for web security (WAF, CDN), developer-centric organizations that want programmable network policy via Cloudflare Workers, and organizations prioritizing network performance alongside security." },
  { q: "What is Cloudflare Workers and when should enterprises use it?", a: "Cloudflare Workers is a serverless computing platform running JavaScript, TypeScript, Python, and WebAssembly on Cloudflare's global edge network — your code runs in 320+ data centers worldwide simultaneously. Unlike traditional serverless platforms (AWS Lambda, Azure Functions) that run in specific regions, Workers run at the nearest Cloudflare location to each user — providing global compute with near-zero latency. Enterprise use cases: Request transformation — rewriting URLs, adding security headers, or transforming API responses before delivery without modifying origin applications. Authentication edge enforcement — validating JWTs, enforcing IP allowlists, or implementing custom auth logic at the edge before requests reach origin infrastructure. A/B testing — serving different application versions to different user segments without origin server involvement. Geographic routing — directing users to different origins based on country, region, or compliance requirements. API gateway — rate limiting, request validation, and response transformation for APIs served through Cloudflare. Bot detection — running ML inference at the edge to classify traffic before it reaches application infrastructure. ALG Data Guard provides Workers development services for organizations needing custom edge logic that complements Cloudflare's platform capabilities." },
  { q: "How does Cloudflare's Zero Trust solution differ from a VPN?", a: "Traditional VPNs grant network-level access — once authenticated, users can reach any resource on the corporate network, creating significant lateral movement risk if credentials are compromised. Cloudflare Access (the Zero Trust component of Cloudflare One) grants application-level access only — users authenticate with their identity provider (Azure AD, Okta, Google Workspace) and are granted access only to specific applications they're authorized for. Each access request is evaluated independently — user identity, device posture, location, and time of day all factor into the access decision. Key differences: No network access — Zero Trust users never connect to the corporate network; they connect to specific applications through Cloudflare's proxy. No VPN client required for browser-based access — users authenticate through Cloudflare's identity-aware proxy without installing VPN software. Continuous verification — trust isn't granted once at login; it's re-evaluated per request based on real-time signals. ALG Data Guard migrates organizations from VPN to Cloudflare Zero Trust — typically starting with remote access for cloud applications and progressively extending to on-premises applications via Cloudflare Tunnel." },
  { q: "What is Cloudflare's DDoS protection and how does it work at the network level?", a: "Cloudflare's DDoS protection is one of the world's largest — with 321 Tbps of network capacity, any volumetric attack (even multi-hundred-Gbps attacks) is absorbed at Cloudflare's edge before reaching customer infrastructure. How it works: All traffic destined for your IP addresses is routed through Cloudflare's network via BGP anycast — Cloudflare advertises your IP prefixes globally and absorbs traffic at the nearest edge location. For HTTP-layer attacks, Cloudflare's ML models analyze request patterns in real-time, distinguishing legitimate traffic from attack traffic and triggering challenge or block rules against attackers. For network-layer attacks (UDP/TCP floods, SYN floods, reflection attacks), Cloudflare's L3/L4 mitigation happens entirely within Cloudflare's network before traffic reaches customer infrastructure. Key advantages: Unmetered protection — Cloudflare does not charge for DDoS traffic, regardless of attack size. Always-on — DDoS mitigation is active 24/7 without requiring attack thresholds or manual activation. Automatic — mitigation triggers automatically without requiring human intervention during an active attack. ALG Data Guard configures Cloudflare DDoS protection for web applications, APIs, and network infrastructure — including Cloudflare Magic Transit for network-layer BGP advertisement of customer IP ranges." },
  { q: "How does ALG Data Guard deploy and manage Cloudflare environments?", a: "ALG Data Guard provides comprehensive Cloudflare deployment services: Initial configuration: DNS migration to Cloudflare, SSL certificate provisioning, WAF rule configuration, and CDN cache optimization for websites and applications moving behind Cloudflare's proxy. Zero Trust deployment: Cloudflare Access configuration with your identity provider, WARP client deployment via MDM, Gateway web filtering policy design, and Cloudflare Tunnel setup for on-premises applications. SASE migration: Phased replacement of legacy VPN infrastructure with Cloudflare ZTNA — starting with cloud applications, extending to on-premises resources, and deploying Cloudflare Magic WAN for network connectivity. Email security: Cloudflare Area 1 deployment — configuring connection to Microsoft 365 or Google Workspace, tuning detection sensitivity, and establishing quarantine and alert workflows. Ongoing management: WAF rule updates, security event review and response, performance optimization, Zero Trust policy management, and Logpush configuration for SIEM integration. Workers development: Custom edge compute development for redirect logic, authentication middleware, API transformation, and A/B testing use cases requiring programmable network logic." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function Cloudflare() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(320, 2000, metricsInView);
  const c2 = useCounter(100, 1800, metricsInView);
  const c3 = useCounter(6, 2200, metricsInView);
  const c4 = useCounter(99, 2000, metricsInView);

  return (
    <div className="cf-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={cloudflareLogo} alt="Cloudflare" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Global Network Security & Performance Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Cloudflare</h1>
            <p className="pd-identity-desc">
              Cloudflare runs one of the world's largest global networks — spanning 320+ cities across 100+ countries — connecting and protecting millions of websites, APIs, SaaS applications, and enterprise networks. The Cloudflare platform delivers network security, performance, Zero Trust access, and edge computing from a single cloud-native platform replacing legacy appliances, VPNs, and point security products. ALG Data Guard is an authorized Cloudflare partner providing deployment, configuration, and management services for the complete Cloudflare portfolio — from WAF and CDN through Cloudflare One SASE to Workers edge compute development.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="cf-hero">
        <div className="cf-hero__grid" aria-hidden="true" />
        <div className="cf-hero__radial" aria-hidden="true" />
        <div className="cf-hero__inner">
          <div>
            <div className="cf-hero__kicker">
              <span className="cf-hero__kicker-dot" />
              Cloudflare Authorized Partner
            </div>
            <h2 className="cf-hero__title">
              Connect, Protect &<br /><em>Accelerate</em> Everything.
            </h2>
            <p className="cf-hero__body">
              Cloudflare's global network is the platform enterprises use to eliminate legacy security appliances, replace VPNs with Zero Trust, accelerate applications worldwide, and run serverless compute at the edge — all from a single cloud-native control plane. ALG Data Guard designs, deploys, and manages Cloudflare environments for organizations moving to a modern, cloud-first security architecture.
            </p>
            <div className="cf-hero__actions">
              <button className="cf-btn cf-btn--orange" onClick={() => navigate("/contact")}>
                Secure My Organization <ArrowIcon />
              </button>
              <button className="cf-btn cf-btn--ghost" onClick={() => navigate("/contact")}>
                Zero Trust Assessment
              </button>
            </div>
            <div className="cf-hero__proof">
              <div><div className="cf-hero__proof-val">320+</div><div className="cf-hero__proof-label">Cities Worldwide</div></div>
              <div><div className="cf-hero__proof-val">100+</div><div className="cf-hero__proof-label">Countries</div></div>
              <div><div className="cf-hero__proof-val">6Tbps</div><div className="cf-hero__proof-label">Network Capacity</div></div>
              <div><div className="cf-hero__proof-val">~50ms</div><div className="cf-hero__proof-label">Global Avg Latency</div></div>
            </div>
          </div>

          {/* Network status panel */}
          <div className="cf-hero__panel">
            <div className="cf-panel__bar">
              <span className="cf-panel__bar-title">Cloudflare Global Network — Edge Status</span>
              <div className="cf-panel__dots">
                <div className="cf-panel__dot cf-panel__dot--r" />
                <div className="cf-panel__dot cf-panel__dot--y" />
                <div className="cf-panel__dot cf-panel__dot--g" />
              </div>
            </div>
            <div className="cf-panel__network">
              {networkPoPs.map(pop => (
                <div key={pop.city} className="cf-network-row">
                  <span className="cf-network-status" />
                  <span className="cf-network-location">{pop.city}</span>
                  <span className="cf-network-latency">{pop.ms}</span>
                </div>
              ))}
            </div>
            <div className="cf-panel__footer">
              <span>321 cities active</span>
              <span className="cf-panel__footer-live">
                <span style={{ width:"6px", height:"6px", borderRadius:"50%", background:"currentColor", animation:"cfBlink 1.5s ease infinite" }} />
                All systems operational
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── PoP Strip ── */}
      <div className="cf-network-strip">
        <div className="cf-strip__inner">
          {["San Francisco", "London", "Frankfurt", "Singapore", "Tokyo", "Dubai", "Amsterdam", "Chicago", "Toronto", "São Paulo"].map(city => (
            <div key={city} className="cf-strip-item">
              <div className="cf-strip-item__dot" />
              <span className="cf-strip-item__city">{city}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── SASE Platform ── */}
      <section className="cf-sase">
        <div className="cf-shell">
          <div className="cf-sase__head">
            <div className="cf-tag cf-tag--orange"><span className="cf-dot" /> Cloudflare One — SASE Platform</div>
            <h2 className="cf-title cf-sase__title">Security & Networking Converged on the World's Fastest Network</h2>
            <p className="cf-sub" style={{ margin:"0 auto", maxWidth:"640px", color:"var(--cf-muted)" }}>Cloudflare One is a complete SASE platform converging Zero Trust access, Secure Web Gateway, CASB, email security, and network connectivity — all delivered from Cloudflare's global edge, replacing appliances and point products with a single cloud-native platform.</p>
          </div>
          <div className="cf-sase__grid">
            {saseCards.map(card => (
              <div key={card.title} className="cf-sase-card">
                <span className="cf-sase-card__icon">{card.icon}</span>
                <h3 className="cf-sase-card__title">{card.title}</h3>
                <p className="cf-sase-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center", marginTop:"3rem" }}>
            <button className="cf-btn cf-btn--orange" onClick={() => navigate("/contact")}>
              Deploy Cloudflare One <ArrowIcon />
            </button>
          </div>
        </div>
      </section>

      {/* ── Zero Trust ── */}
      <section className="cf-zt">
        <div className="cf-shell">
          <div className="cf-zt__inner">
            <div>
              <div className="cf-tag cf-tag--dark"><span className="cf-dot" /> Zero Trust Architecture</div>
              <h2 className="cf-title cf-zt__title">Replace VPNs with Zero Trust — Application by Application</h2>
              <p className="cf-zt__body">
                Cloudflare Access replaces VPN-granted network access with identity-aware, application-level Zero Trust — verifying every user, device, and request before granting access to any resource. No implicit trust, no lateral movement risk, no VPN performance bottlenecks.
              </p>
              <p className="cf-zt__body">
                ALG Data Guard migrates organizations from legacy VPN infrastructure to Cloudflare Zero Trust using a phased approach — beginning with cloud applications and progressively extending to on-premises applications through Cloudflare Tunnel, reducing migration risk while delivering immediate security improvement.
              </p>
              <div className="cf-zt__pillars">
                {ztPillars.map(p => (
                  <div key={p.title} className="cf-zt-pillar">
                    <span className="cf-zt-pillar__icon">{p.icon}</span>
                    <div>
                      <div className="cf-zt-pillar__title">{p.title}</div>
                      <p className="cf-zt-pillar__desc">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="cf-btn cf-btn--orange" onClick={() => navigate("/contact")}>
                  Plan My VPN to Zero Trust Migration <ArrowIcon />
                </button>
              </div>
            </div>
            <div>
              <div style={{ marginBottom:"1rem" }}>
                <div className="cf-tag cf-tag--dark"><span className="cf-dot" /> Architecture Layers</div>
              </div>
              <div className="cf-zt__arch">
                {ztArchBlocks.map((block, i) => (
                  <React.Fragment key={block.name}>
                    <div className="cf-zt-arch-block">
                      <div className="cf-zt-arch-label">{block.label}</div>
                      <div className="cf-zt-arch-name">{block.name}</div>
                      <div className="cf-zt-arch-desc">{block.desc}</div>
                    </div>
                    {i < ztArchBlocks.length - 1 && <div className="cf-zt-arch-arrow">↓</div>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CDN & Performance ── */}
      <section className="cf-perf">
        <div className="cf-shell">
          <div className="cf-perf__inner">
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&q=80"
              alt="Cloudflare global CDN performance"
              className="cf-perf__img"
            />
            <div>
              <div className="cf-tag cf-tag--light"><span className="cf-dot" /> CDN & Performance</div>
              <h2 className="cf-title cf-perf__title" style={{ color:"var(--cf-text)" }}>Faster Applications. Everywhere. Always.</h2>
              <p className="cf-perf__body">
                Cloudflare's anycast CDN delivers content from the nearest of 320+ global edge locations — cutting TTFB, reducing origin load, and providing end users with consistently fast experiences regardless of their geography. Argo Smart Routing further optimizes connections by selecting the fastest path through Cloudflare's private network backbone rather than the public internet.
              </p>
              <div className="cf-perf__stats">
                <div className="cf-perf-stat"><div className="cf-perf-stat__val">3×</div><div className="cf-perf-stat__label">Faster than origin</div></div>
                <div className="cf-perf-stat"><div className="cf-perf-stat__val">60%</div><div className="cf-perf-stat__label">Bandwidth savings</div></div>
                <div className="cf-perf-stat"><div className="cf-perf-stat__val">99.99%</div><div className="cf-perf-stat__label">Network SLA</div></div>
              </div>
              <div className="cf-perf__features">
                {perfFeatures.map(f => <div key={f} className="cf-perf-feat">{f}</div>)}
              </div>
              <div style={{ marginTop:"1.5rem" }}>
                <button className="cf-btn cf-btn--navy" onClick={() => navigate("/contact")}>
                  Performance Assessment <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Workers & Edge Dev ── */}
      <section className="cf-workers">
        <div className="cf-shell">
          <div className="cf-workers__inner">
            <div>
              <div className="cf-tag cf-tag--dark"><span className="cf-dot" /> Edge Computing & Developer Platform</div>
              <h2 className="cf-title cf-workers__title">Build at the Edge. Deploy Everywhere. Instantly.</h2>
              <p className="cf-workers__body">
                Cloudflare Workers brings serverless computing to the network edge — your code runs simultaneously in 320+ cities worldwide with no cold starts, no container management, and sub-millisecond execution time from every user's nearest location. From request transformation and authentication logic to full serverless applications and AI inference, Workers enables application logic that runs at the network boundary.
              </p>
              <div className="cf-workers__stack">
                {workerItems.map(item => (
                  <div key={item.title} className="cf-worker-item">
                    <span className="cf-worker-item__icon">{item.icon}</span>
                    <div>
                      <div className="cf-worker-item__title">{item.title}</div>
                      <p className="cf-worker-item__desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="cf-btn cf-btn--orange" onClick={() => navigate("/contact")}>
                  Explore Edge Development <ArrowIcon />
                </button>
              </div>
            </div>
            <div>
              <div className="cf-code-block">
                <div className="cf-code-block__bar">
                  <div className="cf-panel__dot cf-panel__dot--r" />
                  <div className="cf-panel__dot cf-panel__dot--y" />
                  <div className="cf-panel__dot cf-panel__dot--g" />
                  <span className="cf-code-block__lang">worker.js</span>
                </div>
                <pre>{`<span class="cf-code-kw">export default</span> {
  <span class="cf-code-kw">async</span> <span class="cf-code-fn">fetch</span>(request, env) {
    <span class="cf-code-comment">// Running in 320+ cities globally</span>
    <span class="cf-code-kw">const</span> country = request.cf.country;
    <span class="cf-code-kw">const</span> url = <span class="cf-code-kw">new</span> URL(request.url);

    <span class="cf-code-comment">// Route by geography</span>
    <span class="cf-code-kw">if</span> (country === <span class="cf-code-str">'EU'</span>) {
      url.hostname = <span class="cf-code-str">'eu.api.example.com'</span>;
    }

    <span class="cf-code-comment">// Enforce Zero Trust auth</span>
    <span class="cf-code-kw">const</span> jwt = request.headers
      .get(<span class="cf-code-str">'CF-Access-JWT-Assertion'</span>);
    <span class="cf-code-kw">if</span> (!jwt) <span class="cf-code-kw">return new</span> Response(
      <span class="cf-code-str">'Unauthorized'</span>, { status: <span class="cf-code-str">401</span> }
    );

    <span class="cf-code-kw">return</span> fetch(url.toString(), request);
  }
}`}</pre>
              </div>
              <img
                src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=900&q=80"
                alt="Cloudflare edge developer platform"
                style={{ width:"100%", borderRadius:"var(--cf-radius-lg)", marginTop:"1.2rem", height:"220px", objectFit:"cover", border:"1px solid var(--cf-border-dark)" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── DDoS & WAF ── */}
      <section className="cf-ddos">
        <div className="cf-shell">
          <div className="cf-ddos__inner">
            <img
              src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?auto=format&fit=crop&w=900&q=80"
              alt="Cloudflare DDoS and WAF protection"
              className="cf-ddos__img"
            />
            <div>
              <div className="cf-tag cf-tag--light"><span className="cf-dot" /> DDoS Protection & WAF</div>
              <h2 className="cf-title cf-ddos__title" style={{ color:"var(--cf-text)" }}>Unmetered DDoS. Always-On WAF. Zero Overhead.</h2>
              <p className="cf-ddos__body">
                Cloudflare absorbs DDoS attacks of any size with no bandwidth caps, no additional charges, and no manual intervention — the world's largest DDoS mitigation network at 321 Tbps capacity. The Cloudflare WAF protects web applications and APIs with continuously updated managed rulesets, ML-based bot management, and flexible custom rules.
              </p>
              <div className="cf-ddos__capabilities">
                {ddosCaps.map(cap => (
                  <div key={cap.title} className="cf-ddos-cap">
                    <span className="cf-ddos-cap__icon">{cap.icon}</span>
                    <div>
                      <div className="cf-ddos-cap__title">{cap.title}</div>
                      <p className="cf-ddos-cap__desc">{cap.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"1.5rem" }}>
                <button className="cf-btn cf-btn--navy" onClick={() => navigate("/contact")}>
                  Configure DDoS & WAF <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Email Security ── */}
      <section className="cf-email">
        <div className="cf-shell">
          <div className="cf-email__inner">
            <div>
              <div className="cf-tag cf-tag--dark"><span className="cf-dot" /> Email Security</div>
              <h2 className="cf-title cf-email__title">Area 1 — Stop Phishing Before It Starts</h2>
              <p className="cf-email__body">
                Cloudflare Area 1 is an email security solution that detects and blocks phishing threats before delivery — using Cloudflare's global threat intelligence and web crawlers to identify malicious infrastructure and campaigns as they're being built, not after they've reached inboxes. Area 1 integrates natively with Microsoft 365 and Google Workspace.
              </p>
              <p className="cf-email__body">
                Unlike traditional email security that scans inbound email after acceptance, Area 1 crawls the open web continuously — discovering phishing infrastructure, malicious domains, and attack campaigns as attackers build them, enabling pre-delivery blocking of novel attacks that signature-based systems miss.
              </p>
              <div className="cf-email__features">
                {emailFeatures.map(f => <div key={f} className="cf-email-feat">{f}</div>)}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="cf-btn cf-btn--orange" onClick={() => navigate("/contact")}>
                  Deploy Area 1 Email Security <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1633265486064-086b219458ec?auto=format&fit=crop&w=900&q=80"
              alt="Cloudflare Area 1 email security"
              className="cf-email__img"
            />
          </div>
        </div>
      </section>

      {/* ── Analytics ── */}
      <section className="cf-analytics">
        <div className="cf-shell">
          <div className="cf-analytics__head">
            <div className="cf-tag cf-tag--orange"><span className="cf-dot" /> Analytics & Observability</div>
            <h2 className="cf-title" style={{ color:"var(--cf-text)" }}>Complete Visibility Across Every Edge Event</h2>
            <p className="cf-sub" style={{ margin:"0 auto", maxWidth:"600px", color:"var(--cf-muted)" }}>Cloudflare provides deep analytics across security, performance, traffic, and Zero Trust — with real-time dashboards and log streaming to your SIEM for correlation with endpoint and identity telemetry.</p>
          </div>
          <div className="cf-analytics__grid">
            {analyticsCards.map(card => (
              <div key={card.title} className="cf-analytics-card">
                <span className="cf-analytics-card__icon">{card.icon}</span>
                <h3 className="cf-analytics-card__title">{card.title}</h3>
                <p className="cf-analytics-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="cf-industries">
        <div className="cf-shell">
          <div className="cf-industries__head">
            <div className="cf-tag cf-tag--dark"><span className="cf-dot" /> Industries</div>
            <h2 className="cf-title">Cloudflare Across Every Sector</h2>
            <p className="cf-sub" style={{ margin:"0 auto", maxWidth:"560px", color:"rgba(255,255,255,0.65)" }}>Cloudflare's platform serves every industry vertical with compliance-aligned security configurations and performance optimization for sector-specific application requirements.</p>
          </div>
          <div className="cf-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="cf-ind-card">
                <span className="cf-ind-card__icon">{ind.icon}</span>
                <h3 className="cf-ind-card__name">{ind.name}</h3>
                <p className="cf-ind-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="cf-metrics" ref={metricsRef}>
        <div className="cf-shell">
          <div className="cf-metrics__inner">
            <div><div className="cf-metric__val">{c1}+</div><div className="cf-metric__label">Edge Cities Worldwide</div></div>
            <div><div className="cf-metric__val">{c2}+</div><div className="cf-metric__label">Countries Covered</div></div>
            <div><div className="cf-metric__val">{c3}Tbps</div><div className="cf-metric__label">Network Capacity</div></div>
            <div><div className="cf-metric__val">{c4}.99%</div><div className="cf-metric__label">Network Uptime SLA</div></div>
          </div>
        </div>
      </section>

      {/* ── Why ALG ── */}
      <section className="cf-why">
        <div className="cf-shell">
          <div className="cf-why__inner">
            <div>
              <div className="cf-tag cf-tag--light"><span className="cf-dot" /> Why ALG Data Guard</div>
              <h2 className="cf-title cf-why__title">Cloudflare Expertise, End to End.</h2>
              <p className="cf-why__body">
                Cloudflare's platform spans security, networking, performance, and edge compute — each domain requiring specific configuration expertise. ALG Data Guard's Cloudflare-certified engineers deploy and manage complete Cloudflare environments across all product areas, from DNS and CDN through Zero Trust and Workers development.
              </p>
              <div className="cf-why__reasons">
                {whyReasons.map(r => (
                  <div key={r.num} className="cf-why-reason">
                    <div className="cf-why-reason__num">{r.num}</div>
                    <div>
                      <div className="cf-why-reason__title">{r.title}</div>
                      <p className="cf-why-reason__desc">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button className="cf-btn cf-btn--orange" onClick={() => navigate("/contact")}>
                Start My Cloudflare Project <ArrowIcon />
              </button>
            </div>
            <img
              src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=900&q=80"
              alt="ALG Data Guard Cloudflare expertise"
              className="cf-why__img"
            />
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="cf-faq">
        <div className="cf-shell">
          <div className="cf-faq__head">
            <div className="cf-tag cf-tag--orange" style={{ margin:"0 auto 1rem" }}><span className="cf-dot" /> FAQs</div>
            <h2 className="cf-title" style={{ color:"var(--cf-text)" }}>Cloudflare Questions, Answered</h2>
            <p className="cf-sub" style={{ margin:"0 auto", maxWidth:"560px", color:"var(--cf-muted)" }}>Expert answers from ALG Data Guard's Cloudflare-certified engineers on SASE, Zero Trust, Workers, DDoS, and CDN deployments.</p>
          </div>
          <div className="cf-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`cf-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="cf-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="cf-faq-item__q">{faq.q}</span>
                  <span className="cf-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="cf-faq-item__answer"><div className="cf-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cf-cta">
        <div className="cf-cta__inner">
          <div className="cf-tag cf-tag--dark" style={{ margin:"0 auto 1.2rem" }}><span className="cf-dot" /> Get Started</div>
          <h2 className="cf-cta__title">
            Secure, Accelerate, and <span>Connect Everything</span> with Cloudflare.
          </h2>
          <p className="cf-cta__sub">Whether you're replacing a VPN with Zero Trust, migrating to Cloudflare CDN, deploying Area 1 email security, or building edge applications with Workers — ALG Data Guard's Cloudflare-certified engineers design and deliver the complete Cloudflare platform for your organization.</p>
          <div className="cf-cta__actions">
            <button className="cf-btn cf-btn--orange" onClick={() => navigate("/contact")}>
              Schedule Security Review <ArrowIcon />
            </button>
            <button className="cf-btn cf-btn--ghost" onClick={() => navigate("/contact")}>
              Performance Assessment
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
